import { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { useProfile } from './useProfile';
import { 
  SubscriptionTier, 
  BillingInterval,
  STRIPE_PRICES, 
  getSubscriptionTier,
  TIER_FEATURES,
  hasFeatureAccess,
  getRequiredTier,
  type TierFeatures,
} from '@/lib/subscription-config';

interface SubscriptionState {
  isLoading: boolean;
  tier: SubscriptionTier;
  planType: BillingInterval | null;
  subscriptionEnd: string | null;
  paymentFailed: boolean;
  gracePeriodEnd: string | null;
  productId: string | null;
}

interface SubscriptionContextType extends SubscriptionState {
  // Convenience accessors
  isPremium: boolean; // Has any paid subscription
  isPro: boolean;
  isFullPremium: boolean; // Has Premium tier specifically
  
  // Feature access
  hasFeature: (feature: keyof TierFeatures) => boolean;
  getRequiredTierForFeature: (feature: keyof TierFeatures) => SubscriptionTier;
  
  // Actions
  checkSubscription: () => Promise<void>;
  createCheckout: (priceId: string) => Promise<void>;
  openCustomerPortal: () => Promise<void>;
}

const SubscriptionContext = createContext<SubscriptionContextType | undefined>(undefined);

// DEV MODE: Set to true to preview premium features during development
// IMPORTANT: Must be false in production to enforce subscription checks
const DEV_PREMIUM_MODE = false;
const DEV_TIER: SubscriptionTier = 'premium'; // Which tier to simulate in dev mode

// SECURITY: Founder access removed from client code.
// Premium access is now enforced server-side via edge functions checking profiles.is_premium.
// To grant premium access, set is_premium = true in the profiles table using the database.

export function SubscriptionProvider({ children }: { children: ReactNode }) {
  const { user } = useAuth();
  const { profile } = useProfile();
  const [state, setState] = useState<SubscriptionState>({
    isLoading: false,
    tier: DEV_PREMIUM_MODE ? DEV_TIER : 'free',
    planType: DEV_PREMIUM_MODE ? 'monthly' : null,
    subscriptionEnd: DEV_PREMIUM_MODE ? new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString() : null,
    paymentFailed: false,
    gracePeriodEnd: null,
    productId: null,
  });

  const checkSubscription = useCallback(async () => {
    // Skip API call in dev premium mode
    if (DEV_PREMIUM_MODE) {
      setState({
        isLoading: false,
        tier: DEV_TIER,
        planType: 'monthly',
        subscriptionEnd: new Date(Date.now() + 30 * 24 * 60 * 60 * 1000).toISOString(),
        paymentFailed: false,
        gracePeriodEnd: null,
        productId: null,
      });
      return;
    }

    if (!user) {
      setState({
        isLoading: false,
        tier: 'free',
        planType: null,
        subscriptionEnd: null,
        paymentFailed: false,
        gracePeriodEnd: null,
        productId: null,
      });
      return;
    }

    // If profile is marked premium in database (set via edge functions/admin),
    // treat as Premium. This is a UI convenience - actual enforcement is server-side.
    if (profile?.is_premium) {
      setState({
        isLoading: false,
        tier: 'premium',
        planType: 'monthly',
        subscriptionEnd: new Date(Date.now() + 365 * 24 * 60 * 60 * 1000).toISOString(),
        paymentFailed: false,
        gracePeriodEnd: null,
        productId: null,
      });
      return;
    }

    try {
      setState(prev => ({ ...prev, isLoading: true }));
      
      const { data, error } = await supabase.functions.invoke('check-subscription');
      
      if (error) throw error;

      // Determine tier from product ID
      const tier = getSubscriptionTier(data.product_id);

      setState({
        isLoading: false,
        tier: data.subscribed ? tier : 'free',
        planType: data.plan_type ?? null,
        subscriptionEnd: data.subscription_end ?? null,
        paymentFailed: data.payment_failed ?? false,
        gracePeriodEnd: data.grace_period_end ?? null,
        productId: data.product_id ?? null,
      });
    } catch (error) {
      console.error('Error checking subscription:', error);
      setState({
        isLoading: false,
        tier: 'free',
        planType: null,
        subscriptionEnd: null,
        paymentFailed: false,
        gracePeriodEnd: null,
        productId: null,
      });
    }
  }, [user, profile?.is_premium]);

  const createCheckout = useCallback(async (priceId: string) => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('create-checkout', {
      body: { price_id: priceId },
    });

    if (error) throw error;
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [user]);

  const openCustomerPortal = useCallback(async () => {
    if (!user) throw new Error('User not authenticated');

    const { data, error } = await supabase.functions.invoke('customer-portal');

    if (error) throw error;
    if (data?.url) {
      window.open(data.url, '_blank');
    }
  }, [user]);

  // Check subscription on mount and when user changes
  useEffect(() => {
    checkSubscription();
  }, [checkSubscription]);

  // Refresh subscription status every 60 seconds
  useEffect(() => {
    if (!user) return;
    
    const interval = setInterval(checkSubscription, 60000);
    return () => clearInterval(interval);
  }, [user, checkSubscription]);

  // Convenience accessors
  const isPremium = state.tier !== 'free';
  const isPro = state.tier === 'pro' || state.tier === 'premium';
  const isFullPremium = state.tier === 'premium';

  // Feature access helpers
  const hasFeature = useCallback((feature: keyof TierFeatures): boolean => {
    return hasFeatureAccess(state.tier, feature);
  }, [state.tier]);

  const getRequiredTierForFeature = useCallback((feature: keyof TierFeatures): SubscriptionTier => {
    return getRequiredTier(feature);
  }, []);

  return (
    <SubscriptionContext.Provider value={{
      ...state,
      isPremium,
      isPro,
      isFullPremium,
      hasFeature,
      getRequiredTierForFeature,
      checkSubscription,
      createCheckout,
      openCustomerPortal,
    }}>
      {children}
    </SubscriptionContext.Provider>
  );
}

export function useSubscription() {
  const context = useContext(SubscriptionContext);
  if (context === undefined) {
    throw new Error('useSubscription must be used within a SubscriptionProvider');
  }
  return context;
}

// Re-export for convenience
export { STRIPE_PRICES, type SubscriptionTier, type BillingInterval };

// Premium feature gate component
interface PremiumGateProps {
  children: ReactNode;
  fallback?: ReactNode;
  showLocked?: boolean;
  requiredTier?: SubscriptionTier;
}

export function PremiumGate({ 
  children, 
  fallback, 
  showLocked = false,
  requiredTier = 'pro',
}: PremiumGateProps) {
  const { tier, isLoading } = useSubscription();

  if (isLoading) {
    return null;
  }

  const tierOrder: SubscriptionTier[] = ['free', 'pro', 'premium'];
  const hasAccess = tierOrder.indexOf(tier) >= tierOrder.indexOf(requiredTier);

  if (!hasAccess) {
    if (showLocked && fallback) {
      return <>{fallback}</>;
    }
    return null;
  }

  return <>{children}</>;
}

// Feature-specific gate
interface FeatureGateProps {
  feature: keyof TierFeatures;
  children: ReactNode;
  fallback?: ReactNode;
}

export function FeatureGate({ feature, children, fallback }: FeatureGateProps) {
  const { hasFeature, isLoading } = useSubscription();

  if (isLoading) {
    return null;
  }

  if (!hasFeature(feature)) {
    return fallback ? <>{fallback}</> : null;
  }

  return <>{children}</>;
}
